import { InlineMarkdown } from "@/components/InlineMarkdown";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

// Block-level markdown for chat messages: paragraphs and ordered/unordered
// lists, with inline formatting (bold/italic/code/links/autolinked URLs)
// delegated to InlineMarkdown. Deliberately small — Boo replies are prose plus
// simple lists, not full CommonMark (no tables, blockquotes, or fenced code).
// XSS-safe by construction: it builds React nodes, never sets innerHTML.

const OL_RE = /^\s*(\d+)\.\s+(.*)$/;
const UL_RE = /^\s*[-*]\s+(.*)$/;

// A blank line between two list items is a "loose list" — the items belong to
// the same list, not two adjacent ones. Peek past blanks to decide.
function nextIsItem(lines: string[], from: number, re: RegExp): boolean {
  let j = from;
  while (j < lines.length && lines[j].trim() === "") j += 1;
  return j < lines.length && re.test(lines[j]);
}

export function MessageMarkdown({ text, className }: { text: string; className?: string }) {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    if (lines[i].trim() === "") {
      i += 1;
      continue;
    }

    if (OL_RE.test(lines[i])) {
      const items: { n: number; text: string }[] = [];
      while (i < lines.length) {
        const m = lines[i].match(OL_RE);
        if (m) {
          items.push({ n: Number(m[1]), text: m[2] });
          i += 1;
        } else if (lines[i].trim() === "" && nextIsItem(lines, i + 1, OL_RE)) {
          i += 1; // loose-list blank separator
        } else {
          break;
        }
      }
      blocks.push(
        <ol key={key++} className="list-decimal space-y-1 pl-5">
          {items.map((it, j) => (
            // value keeps the source ordinal even if the scanner starts >1.
            <li key={j} value={it.n}>
              <InlineMarkdown text={it.text} />
            </li>
          ))}
        </ol>,
      );
      continue;
    }

    if (UL_RE.test(lines[i])) {
      const items: string[] = [];
      while (i < lines.length) {
        const m = lines[i].match(UL_RE);
        if (m) {
          items.push(m[1]);
          i += 1;
        } else if (lines[i].trim() === "" && nextIsItem(lines, i + 1, UL_RE)) {
          i += 1;
        } else {
          break;
        }
      }
      blocks.push(
        <ul key={key++} className="list-disc space-y-1 pl-5">
          {items.map((it, j) => (
            <li key={j}>
              <InlineMarkdown text={it} />
            </li>
          ))}
        </ul>,
      );
      continue;
    }

    // Paragraph: consecutive non-blank, non-list lines. Single newlines inside
    // a paragraph become <br> (chat replies rely on soft line breaks).
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() !== "" && !OL_RE.test(lines[i]) && !UL_RE.test(lines[i])) {
      para.push(lines[i]);
      i += 1;
    }
    blocks.push(
      <p key={key++}>
        {para.map((ln, j) => (
          <span key={j}>
            {j > 0 && <br />}
            <InlineMarkdown text={ln} />
          </span>
        ))}
      </p>,
    );
  }

  return <div className={cn("space-y-2", className)}>{blocks}</div>;
}
